// ═══════════════════════════════════════════════════════════════
//  routes.js — correspondance route ⟷ ancre d'URL (lot T8)
//
//  ⚠️ N'IMPORTE RIEN et n'accède PAS à `window` : ce fichier doit rester
//  chargeable par `node` seul pour son test (même parti pris que
//  affectation.js, workflow.js, versPlanning.js, tachesRequetes.js). La
//  lecture de `location.hash` est faite par l'appelant, App.jsx.
//
//  Objet : qu'une adresse désigne un écran. « /#/taches » ouvre le module
//  Tâches — c'est ce qui rend un raccourci PWA et un lien envoyé par SMS
//  utilisables. L'app reste mono-page, sans routeur : le hash ne part jamais
//  au serveur, donc aucune réécriture Vercel n'est concernée.
//
//  🔴 hasOwnProperty PARTOUT. Sans lui, l'ancre « #/constructor » ou la route
//  « toString » renverraient une fonction héritée d'Object.prototype, et
//  l'appelant partirait avec une « route » qui n'existe pas.
// ═══════════════════════════════════════════════════════════════

// Les 18 routes de App.jsx, et elles seules. Toute route ajoutée là-bas se
// déclare ici ET dans routes.test.js — sinon test:unit échoue, c'est voulu.
export const ROUTE_ANCRES = Object.freeze({
  "home":                   "/",
  "module:commandes":       "/commandes",
  "module:avancement":      "/avancement",
  "module:gestionChantier": "/gestion-chantier",
  "module:planning":        "/planning",
  "module:rh":              "/rh",
  "module:taches":          "/taches",
  "module:chiffrage":       "/chiffrage",
  "module:carnetMateriel":  "/carnet-materiel",
  "module:interventions":   "/interventions",
  "module:parc-machines":   "/parc-machines",
  "module:reserves":        "/reserves",
  "dashboard:direction":    "/cockpit",
  "dashboard:conducteur":   "/dashboard-conducteur",
  "dashboard:public":       "/dashboard-public",
  "dashboards":             "/dashboards",
  "admin":                  "/admin",
  "change-password":        "/mot-de-passe",
});

// Index inverse construit UNE fois — pas de scan à chaque hashchange.
const ANCRE_ROUTES = Object.freeze(
  Object.fromEntries(Object.entries(ROUTE_ANCRES).map(([r, a]) => [a, r])),
);
const a_la_cle = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k);

export function ancreDeRoute(route) {
  return a_la_cle(ROUTE_ANCRES, route) ? ROUTE_ANCRES[route] : null;
}

// "#/taches" | "/taches" | "taches" | "#/Taches/" | "#/taches?x=1" -> "/taches"
export function normaliserAncre(brut) {
  if (typeof brut !== "string") return null;
  let s = brut.trim();
  if (s.startsWith("#")) s = s.slice(1);
  const coupe = s.search(/[?&]/);
  if (coupe !== -1) s = s.slice(0, coupe);
  if (!s.startsWith("/")) s = "/" + s;
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  return s.toLowerCase();
}

// Routes qui acceptent un identifiant en second segment :
// « #/interventions/<id> » ouvre la fiche (bannière d'accueil, planning) ;
// « #/planning/<AAAA-MM-JJ> » ouvre la semaine qui contient la date
// (bannière « créneaux à réaffecter ») — validée par l'écran Planning
// (dateDepuisAncrePlanning), une date invalide ouvre la semaine courante.
const ROUTES_A_IDENTIFIANT = Object.freeze(["module:interventions", "module:planning"]);

// 🔴 Hash ABSENT ("" ou "#") renvoie null, PAS "home" : c'est ce qui préserve
// le repli localStorage d'avant T8. Seule l'ancre EXPLICITE "#/" vaut accueil.
export function routeDepuisAncre(brut) {
  if (typeof brut !== "string" || brut === "" || brut === "#") return null;
  const a = normaliserAncre(brut);
  if (a_la_cle(ANCRE_ROUTES, a)) return ANCRE_ROUTES[a];
  const m = /^(\/[a-z0-9-]+)\/[^/]+$/.exec(a || "");
  if (m && a_la_cle(ANCRE_ROUTES, m[1]) && ROUTES_A_IDENTIFIANT.includes(ANCRE_ROUTES[m[1]])) {
    return ANCRE_ROUTES[m[1]];
  }
  return null;
}

// Identifiant porté par l'ancre (« #/interventions/AbC12 » → "AbC12").
// 🔴 Lu sur l'ancre BRUTE, pas normalisée : un id Firestore est sensible à
// la casse, normaliserAncre le mettrait en minuscules. null sinon.
export function idDepuisAncre(brut) {
  if (typeof brut !== "string" || routeDepuisAncre(brut) == null) return null;
  let s = brut.trim();
  if (s.startsWith("#")) s = s.slice(1);
  const coupe = s.search(/[?&]/);
  if (coupe !== -1) s = s.slice(0, coupe);
  const segs = s.split("/").filter(Boolean);
  if (segs.length !== 2) return null;
  try { return decodeURIComponent(segs[1]) || null; } catch { return null; }
}

// Ancre d'une fiche : ancreAvecId("module:interventions", "AbC") → "/interventions/AbC".
export function ancreAvecId(route, id) {
  const base = ancreDeRoute(route);
  if (!base || !ROUTES_A_IDENTIFIANT.includes(route)) return null;
  const v = id != null ? String(id).trim() : "";
  return v ? `${base}/${encodeURIComponent(v)}` : base;
}
